/**
 * Duelist Tank — Close quarters specialist. Iaijutsu dash-strike + tipper arc.
 * Shot 1: Charge → dash through opponent → frozen moment → "SLASH!" resolve.
 * Shot 2: Tipper — overhead arc sweep, carves terrain, tip zone is devastating.
 */

import { registerWeapon } from '../weapons';
import { clearCircle, applyScorch } from '../terrain/grid';
import { DUELIST_SLASH, KNOCKBACK, WORLD, PHYSICS } from '../constants';
import { mirrorDIFreeze } from '../physics/transitions';
import { cancelGrapple } from '../game/grapple';
import { recordTrickHit } from '../game/trick-score';
import { registerTank } from './registry';
import { DEFAULT_CRATER, SMALL_CRATER } from '../terrain/crater-shapes';

/** Convert milliseconds to fixed-update frame count (60fps). */
const msToFrames = (ms: number) => Math.round(ms * 60 / 1000);

// ── Phase IDs (behaviorState is Record<string, number>) ──
const PHASE_STARTUP = 0;
const PHASE_DASH = 1;
const PHASE_PAUSE = 2;
const PHASE_RESOLVE = 3;
const PHASE_WHIFF = 4;

/** Smoothstep ease-out: fast start, decelerate at end. */
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

// Shot 1: Iaijutsu Dash-Strike — charge → startup → dash → hit/whiff fork
const DUELIST_SHOT1 = registerWeapon({
  id: 'duelist_shot1',
  name: 'Slash',
  description: 'Iaijutsu dash-strike. Charge longer for more reach and damage.',
  tier: 'CHEAP',
  rarity: 'common',
  projectileRadius: 3,
  projectileSpeed: 0.01, // near-zero — proj is invisible lifecycle manager
  color: 0xff2266,
  gravity: 0,
  bounces: 0,
  bounceEnergyRetention: 0,
  damage: DUELIST_SLASH.BASE_DAMAGE,
  blastRadius: 80,
  knockbackMult: 0.10,
  knockbackRadius: 1.2,
  shakeIntensity: 3.0,
  particleScale: 1.0,
  noCrater: true,
  craterShape: SMALL_CRATER,
  tracerMode: 'dash',
  edgeguard: { chargeFrames: 45, threshold: 50 },
  onTick(proj, state, fx) {
    proj.ignoreTerrain = true;
    proj.ignoreTanks = true;
    proj.ignoreWind = true;

    const owner = state.players[proj.owner];
    if (!owner) return 'consume';

    const bs = proj.behaviorState;

    // ── First frame: initialize phase state ──
    if (bs.initialized === undefined) {
      bs.initialized = 1;
      bs.phase = PHASE_STARTUP;
      bs.frame = 0;
      bs.hitPlayerIndex = -1;

      const chargePower = bs.chargePower ?? 0;
      const angle = -owner.angle; // screen-space angle

      // Determine tier from charge
      let tier = 0;
      if (chargePower >= DUELIST_SLASH.LONG_THRESHOLD) tier = 2;
      else if (chargePower >= DUELIST_SLASH.MID_THRESHOLD) tier = 1;

      bs.tier = tier;
      bs.dashAngle = angle;
      bs.cosA = Math.cos(angle);
      bs.sinA = Math.sin(angle);
      bs.startX = owner.x;
      bs.startY = owner.y;

      // Tier-dependent values
      if (tier === 2) {
        bs.dashDist = DUELIST_SLASH.LONG_RANGE;
        bs.dashFrames = DUELIST_SLASH.LONG_FRAMES;
        bs.damage = DUELIST_SLASH.LONG_DAMAGE;
        bs.kbMult = DUELIST_SLASH.LONG_KB;
      } else if (tier === 1) {
        bs.dashDist = DUELIST_SLASH.MID_RANGE;
        bs.dashFrames = DUELIST_SLASH.MID_FRAMES;
        bs.damage = DUELIST_SLASH.MID_DAMAGE;
        bs.kbMult = DUELIST_SLASH.MID_KB;
      } else {
        bs.dashDist = DUELIST_SLASH.SHORT_RANGE;
        bs.dashFrames = DUELIST_SLASH.SHORT_FRAMES;
        bs.damage = DUELIST_SLASH.SHORT_DAMAGE;
        bs.kbMult = DUELIST_SLASH.SHORT_KB;
      }

      // Clamp dash endpoint to world bounds
      let endX = bs.startX + bs.cosA * bs.dashDist;
      let endY = bs.startY + bs.sinA * bs.dashDist;
      endX = Math.max(WORLD.WORLD_LEFT + PHYSICS.ECB_RADIUS, Math.min(WORLD.WORLD_RIGHT - PHYSICS.ECB_RADIUS, endX));
      endY = Math.max(WORLD.WORLD_TOP + PHYSICS.ECB_RADIUS, endY);
      bs.endX = endX;
      bs.endY = endY;

      // Enable dash immunity
      owner.dashImmune = 1;

      // Startup VFX: converging energy
      fx.push({ type: 'particles', method: 'spawnDuelistStartup', args: [owner.x, owner.y, angle] });
    }

    const phase = bs.phase;
    const frame = bs.frame;

    // ══════════════════════════════════════════════════════
    // Phase 0: STARTUP (3 frames)
    // ══════════════════════════════════════════════════════
    if (phase === PHASE_STARTUP) {
      // Lock owner in place
      owner.x = bs.startX;
      owner.y = bs.startY;
      owner.vx = 0;
      owner.vy = 0;
      owner.settled = false;
      // Camera follows owner
      proj.x = owner.x;
      proj.y = owner.y;

      if (frame === DUELIST_SLASH.STARTUP_FRAMES - 1) {
        // Shockwave at feet on final startup frame
        fx.push({ type: 'particles', method: 'spawnShockwave', args: [owner.x, owner.y, 0xff2266, 30, 200, 2, 0.1] });
      }

      bs.frame++;
      if (bs.frame >= DUELIST_SLASH.STARTUP_FRAMES) {
        bs.phase = PHASE_DASH;
        bs.frame = 0;
        // Sonic boom on dash start
        fx.push({ type: 'particles', method: 'spawnDuelistDashStart', args: [owner.x, owner.y, bs.dashAngle] });
        fx.push({ type: 'screen_shake', intensity: 4, decay: 0.9 });
      }
      return null;
    }

    // ══════════════════════════════════════════════════════
    // Phase 1: DASH (8-12 frames, tier-dependent)
    // ══════════════════════════════════════════════════════
    if (phase === PHASE_DASH) {
      const t = Math.min(1, (frame + 1) / bs.dashFrames);
      const eased = easeOutCubic(t);

      // Move owner along dash line
      owner.x = bs.startX + (bs.endX - bs.startX) * eased;
      owner.y = bs.startY + (bs.endY - bs.startY) * eased;
      owner.vx = 0;
      owner.vy = 0;
      owner.settled = false;

      // Camera follows
      proj.x = owner.x;
      proj.y = owner.y;

      // Afterimage particles every 2 frames
      if (frame % 2 === 0) {
        fx.push({ type: 'particles', method: 'spawnDuelistAfterimage', args: [owner.x, owner.y, bs.dashAngle, owner.color] });
      }

      // Speed line particles every frame
      fx.push({ type: 'particles', method: 'spawnDuelistSpeedLines', args: [owner.x, owner.y, bs.dashAngle] });

      // Terrain carve — blade slice at current position (Trencher-style)
      const carveBounds = clearCircle(owner.x, owner.y, DUELIST_SLASH.CARVE_RADIUS);
      if (carveBounds) {
        applyScorch(owner.x, owner.y, DUELIST_SLASH.CARVE_RADIUS);
        fx.push({ type: 'terrain_update', bounds: carveBounds });
        fx.push({ type: 'particles', method: 'spawnTerrainDebris', args: [owner.x, owner.y, DUELIST_SLASH.CARVE_RADIUS, 0.12] });
      }

      // ── Hit detection: sweep check per frame ──
      if (bs.hitPlayerIndex < 0) {
        const cosA = bs.cosA;
        const sinA = bs.sinA;

        for (let i = 0; i < state.players.length; i++) {
          if (i === proj.owner) continue;
          const p = state.players[i];
          if (!p.alive) continue;

          // Perpendicular distance from dash line
          const dx = p.x - bs.startX;
          const dy = p.y - bs.startY;
          const alongDash = dx * cosA + dy * sinA;
          // Must be between start and current position (+ small buffer)
          const currentDist = bs.dashDist * eased;
          if (alongDash < -20 || alongDash > currentDist + 20) continue;
          const perpDist = Math.abs(dx * sinA - dy * cosA);
          if (perpDist > DUELIST_SLASH.HIT_PERPENDICULAR) continue;

          // HIT! Apply damage immediately, store knockback for DI
          bs.hitPlayerIndex = i;
          bs.hitX = p.x;
          bs.hitY = p.y;

          p.damagePercent += bs.damage;

          // Match stat tracking
          if (proj.owner >= 0 && proj.owner !== i && state.matchStats) {
            state.matchStats.damageDealt[proj.owner] += bs.damage;
            state.matchStats.damageTaken[i] += bs.damage;
            if (bs.damage > state.matchStats.bestSingleHit[proj.owner]) {
              state.matchStats.bestSingleHit[proj.owner] = bs.damage;
            }
            p.lastHitBy = proj.owner;
          }

          // Knockback along dash direction
          if (state.grapple.active?.playerIndex === i) cancelGrapple(state);
          const percentMult = 1 + p.damagePercent / KNOCKBACK.KBG;
          const kbForce = proj.weapon.blastRadius * bs.kbMult * percentMult;
          p.diPendingVx = cosA * kbForce;
          p.diPendingVy = sinA * kbForce;
          mirrorDIFreeze(p);
          p.settled = false;
          state.firingPhaseKnockedPlayers.push(i);

          // Hit contact VFX
          fx.push({ type: 'tank_flash', playerIndex: i, bright: 0xff4444, dark: 0x220000, duration: 0.4 });
          fx.push({ type: 'particles', method: 'spawnHitCross', args: [p.x, p.y, 40] });
          fx.push({ type: 'screen_shake', intensity: 8, decay: 0.82 });

          // Trick recording — slash is always a direct hit
          recordTrickHit(state, proj, i, p.x, p.y, bs.damage, true, fx);

          break; // First contact only
        }
      }

      bs.frame++;
      if (bs.frame >= bs.dashFrames) {
        // Dash complete — clear immunity
        owner.dashImmune = 0;

        if (bs.hitPlayerIndex >= 0) {
          // Hit → dramatic pause
          bs.phase = PHASE_PAUSE;
          bs.frame = 0;
          // Spawn blade trail particles along dash path
          fx.push({ type: 'particles', method: 'spawnDuelistBladeTrail', args: [bs.startX, bs.startY, bs.endX, bs.endY, bs.dashAngle] });
        } else {
          // Whiff → recovery
          bs.phase = PHASE_WHIFF;
          bs.frame = 0;
          // Brief freeze
          state.freezeUntil = Math.max(state.freezeUntil, state.frameCount + msToFrames(DUELIST_SLASH.WHIFF_FREEZE_MS));
          // Weak shockwave + dust at destination
          fx.push({ type: 'particles', method: 'spawnExplosion', args: [owner.x, owner.y, 0x665555, 6] });
          fx.push({ type: 'screen_shake', intensity: 2, decay: 0.92 });
        }
      }
      return null;
    }

    // ══════════════════════════════════════════════════════
    // Phase 2: DRAMATIC PAUSE (40 frames on hit)
    // ══════════════════════════════════════════════════════
    if (phase === PHASE_PAUSE) {
      // Owner locked at dash endpoint
      owner.x = bs.endX;
      owner.y = bs.endY;
      owner.vx = 0;
      owner.vy = 0;
      owner.settled = false;
      proj.x = owner.x;
      proj.y = owner.y;

      // Victim pulses with flash
      if (bs.hitPlayerIndex >= 0 && frame % 8 === 0) {
        fx.push({ type: 'tank_flash', playerIndex: bs.hitPlayerIndex, bright: 0xff2266, dark: 0x110000, duration: 0.12 });
      }

      // Building tension: faint shake ramps
      if (frame === 25) {
        fx.push({ type: 'screen_shake', intensity: 3, decay: 0.95 });
      }
      if (frame === 35) {
        fx.push({ type: 'screen_shake', intensity: 6, decay: 0.92 });
      }

      // Pre-resolve flash on last frame
      if (frame === DUELIST_SLASH.PAUSE_FRAMES - 1) {
        fx.push({ type: 'screen_flash', color: 0xffffff, alpha: 0.25 });
      }

      bs.frame++;
      if (bs.frame >= DUELIST_SLASH.PAUSE_FRAMES) {
        bs.phase = PHASE_RESOLVE;
        bs.frame = 0;
      }
      return null;
    }

    // ══════════════════════════════════════════════════════
    // Phase 3: SLASH RESOLVE (instant, then hitlag)
    // ══════════════════════════════════════════════════════
    if (phase === PHASE_RESOLVE) {
      // Real hitlag
      state.freezeUntil = Math.max(state.freezeUntil, state.frameCount + msToFrames(DUELIST_SLASH.RESOLVE_HITLAG_MS));

      // Announcement
      const isLong = bs.tier === 2;
      const label = isLong ? 'ISSEN!' : 'SLASH!';
      const fontSize = isLong ? 44 : 38;
      const hold = isLong ? 1.2 : 1.0;
      const hitX = bs.hitX;
      const hitY = bs.hitY;
      fx.push({ type: 'announce_world', text: label, x: hitX, y: hitY - 50, color: 0xff2266, fontSize, hold });

      // Screen flash: white → crimson
      fx.push({ type: 'screen_flash', color: 0xffffff, alpha: 0.3 });
      fx.push({ type: 'screen_flash', color: 0xff2266, alpha: 0.2 });

      // Screen shake
      fx.push({ type: 'screen_shake', intensity: 20, decay: 0.82 });

      // Resolve VFX: blade trail shatter + shockwave at victim
      fx.push({ type: 'particles', method: 'spawnDuelistSlashResolve', args: [hitX, hitY, bs.startX, bs.startY, bs.endX, bs.endY, bs.tier] });

      // Hit cross at victim
      fx.push({ type: 'particles', method: 'spawnHitCross', args: [hitX, hitY, 60] });

      // Tank flash on victim
      if (bs.hitPlayerIndex >= 0) {
        fx.push({ type: 'tank_flash', playerIndex: bs.hitPlayerIndex, bright: 0xff0044, dark: 0x220000, duration: 0.5 });
      }

      return 'consume';
    }

    // ══════════════════════════════════════════════════════
    // Phase 4: WHIFF RECOVERY (15 frames)
    // ══════════════════════════════════════════════════════
    if (phase === PHASE_WHIFF) {
      // Owner locked at destination
      owner.x = bs.endX;
      owner.y = bs.endY;
      owner.vx = 0;
      owner.vy = 0;
      owner.settled = false;
      proj.x = owner.x;
      proj.y = owner.y;

      bs.frame++;
      if (bs.frame >= DUELIST_SLASH.WHIFF_FRAMES) {
        return 'consume';
      }
      return null;
    }

    // Fallback — shouldn't reach here
    return 'consume';
  },
});

// Shot 2: Tipper — overhead arc sweep, starts 90° above, arcs down and forward
// Carves terrain along the arc path. Tip explodes at the endpoint.
// Tipper zone: the bottom of the arc where the blade meets the ground.
const DUELIST_SHOT2 = registerWeapon({
  id: 'duelist_shot2',
  name: 'Tipper',
  description: 'Overhead arc. Carves terrain along the sweep. Tip explodes.',
  tier: 'UNCOMMON',
  rarity: 'uncommon',
  projectileRadius: 5,
  projectileSpeed: 0.55,
  color: 0xff0044,
  gravity: 0.15,
  bounces: 0,
  bounceEnergyRetention: 0.85,
  damage: 22,
  blastRadius: 110,
  knockbackMult: 0.12,
  knockbackRadius: 1.4,
  shakeIntensity: 2.5,
  particleScale: 1.5,
  craterShape: DEFAULT_CRATER,
  tracerMode: 'sweep',
  edgeguard: { chargeFrames: 45, threshold: 50 },
  onTick(proj, state, fx) {
    proj.ignoreTerrain = true;
    proj.ignoreTanks = true;

    const owner = state.players[proj.owner];
    if (!owner) return 'consume';

    // Arc sweep: 90° arc from straight up to horizontal-forward
    // Radius of the arc (blade reach)
    const arcRadius = 180;
    const facingRight = Math.cos(owner.angle) >= 0;
    const dirX = facingRight ? 1 : -1;
    const baseX = owner.x;
    const baseY = owner.y;

    // Sample points along the arc: angle goes from -PI/2 (straight up)
    // to 0 (horizontal forward). 20 sample points for terrain carving + hit detection.
    const arcSteps = 20;
    const arcPoints: { x: number; y: number; t: number }[] = [];
    for (let step = 0; step <= arcSteps; step++) {
      const t = step / arcSteps; // 0=top, 1=bottom/forward
      // Angle: -PI/2 (up) to 0 (horizontal right/left)
      const arcAngle = -Math.PI / 2 + (Math.PI / 2) * t;
      const px = baseX + Math.cos(arcAngle) * arcRadius * dirX;
      const py = baseY + Math.sin(arcAngle) * arcRadius;
      arcPoints.push({ x: px, y: py, t });
    }

    // Tip position (end of arc = horizontal forward)
    const tipX = arcPoints[arcSteps].x;
    const tipY = arcPoints[arcSteps].y;

    // Terrain carving along the arc path
    const carveRadius = 25;
    for (const pt of arcPoints) {
      const carveBounds = clearCircle(pt.x, pt.y, carveRadius);
      if (carveBounds) {
        applyScorch(pt.x, pt.y, carveRadius);
        fx.push({ type: 'terrain_update', bounds: carveBounds });
      }
    }
    // Terrain debris at a few key points (not every sample — too many)
    for (let step = 0; step <= arcSteps; step += 5) {
      const pt = arcPoints[step];
      fx.push({ type: 'particles', method: 'spawnTerrainDebris', args: [pt.x, pt.y, carveRadius, 0.3] });
    }

    // Hit detection: check each player against each arc segment
    for (let i = 0; i < state.players.length; i++) {
      if (i === proj.owner) continue;
      const p = state.players[i];
      if (!p.alive) continue;

      // Find closest point on the arc to the player
      let minDist = Infinity;
      let closestT = 0;
      let closestPt = arcPoints[0];
      for (const pt of arcPoints) {
        const dx = p.x - pt.x;
        const dy = p.y - pt.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < minDist) {
          minDist = dist;
          closestT = pt.t;
          closestPt = pt;
        }
      }

      if (minDist < 35) { // hit radius from arc
        // Tipper zones — position along the arc (t: 0=top, 1=tip)
        // Early arc (0-0.6): 8% damage, 0.8x KB — decent
        // Late arc (0.6-0.85): 14% damage, 1.1x KB — solid
        // Tipper (0.85-1.0): 22% damage, 1.5x KB — devastating
        let damage: number;
        let kbMult: number;
        if (closestT >= 0.85) {
          // TIPPER — bottom of the arc, blade meets the ground
          damage = 22;
          kbMult = 1.5;
          fx.push({ type: 'announce_world', text: 'TIPPER!', x: p.x, y: p.y - 60, color: 0xff0044, fontSize: 36, hold: 0.8 });
          fx.push({ type: 'screen_flash', color: 0xffffff, alpha: 0.3 });
          fx.push({ type: 'screen_flash', color: 0xff0044, alpha: 0.2 });
          fx.push({ type: 'screen_shake', intensity: 18, decay: 0.78 });
          fx.push({ type: 'particles', method: 'spawnTipperDetonation', args: [p.x, p.y] });
        } else if (closestT >= 0.6) {
          // Late arc — solid hit
          damage = 14;
          kbMult = 1.1;
          fx.push({ type: 'screen_shake', intensity: 5, decay: 0.88 });
          fx.push({ type: 'particles', method: 'spawnExplosion', args: [p.x, p.y, 0xff2266, 8] });
        } else {
          // Early arc — overhead whiff zone, still hits but weak
          damage = 8;
          kbMult = 0.8;
          fx.push({ type: 'screen_shake', intensity: 2, decay: 0.9 });
          fx.push({ type: 'particles', method: 'spawnExplosion', args: [p.x, p.y, 0xffffff, 3] });
        }

        p.damagePercent += damage;
        // Knockback: push away from the arc contact point (mirrors applyBlastKnockback pipeline)
        if (state.grapple.active?.playerIndex === i) cancelGrapple(state);
        const hitDx = p.x - closestPt.x;
        const hitDy = p.y - closestPt.y;
        const hitDist = Math.max(minDist, 1);
        const percentMult = 1 + p.damagePercent / KNOCKBACK.KBG;
        const kbForce = proj.weapon.blastRadius * proj.weapon.knockbackMult * kbMult * percentMult;
        p.diPendingVx = (hitDx / hitDist) * kbForce;
        p.diPendingVy = (hitDy / hitDist) * kbForce;
        mirrorDIFreeze(p);
        p.settled = false;

        const isTipper = closestT >= 0.85;
        const flashDuration = isTipper ? 0.5 : 0.15;
        fx.push({ type: 'tank_flash', playerIndex: i, bright: isTipper ? 0xff0044 : 0xff4444, dark: 0x220000, duration: flashDuration });
        state.firingPhaseKnockedPlayers.push(i);

        // Trick recording — tipper sweetspot counts as direct hit
        recordTrickHit(state, proj, i, p.x, p.y, damage, isTipper, fx);
      }
    }

    // Tip explosion crater (endpoint of arc)
    const craterRadius = 65;
    const tipCarveBounds = clearCircle(tipX, tipY, craterRadius);
    if (tipCarveBounds) {
      applyScorch(tipX, tipY, craterRadius);
      fx.push({ type: 'terrain_update', bounds: tipCarveBounds });
      fx.push({ type: 'particles', method: 'spawnTerrainDebris', args: [tipX, tipY, craterRadius, 0.8] });
    }

    // Arc trail VFX — burning blade sweep with heating gradient
    fx.push({ type: 'particles', method: 'spawnDuelistArc', args: [baseX, baseY, arcPoints, dirX] });
    // Tip explosion VFX (non-tipper — base detonation at endpoint)
    fx.push({ type: 'particles', method: 'spawnExplosion', args: [tipX, tipY, 0xff0044, 16] });
    fx.push({ type: 'particles', method: 'spawnExplosion', args: [tipX, tipY, 0xffffff, 8] });
    fx.push({ type: 'screen_shake', intensity: 10, decay: 0.88 });
    fx.push({ type: 'screen_flash', color: 0xff0044, alpha: 0.08 });

    return 'consume';
  },
});

registerTank({
  id: 'duelist',
  name: 'Duelist',
  description: 'Close quarters specialist. The blade rewards precision.',
  visual: { bodyColor: 0xCC1133, glowColor: 0xFF2244, bodyRadius: 32 },
  ecbRadius: 22,
  bonus: true,
  chargeSize: 80,
  movementCostMultiplier: 0.75,
  landingMultiplier: 1.3,
  shots: [
    { tier: 1, weapon: DUELIST_SHOT1, momentumCost: 0, hitlagBonus: 0, label: 'Slash', tierColor: 0xff2266, instantFire: false, chargeMode: 'linear', chargeRate: DUELIST_SLASH.CHARGE_RATE },
    { tier: 2, weapon: DUELIST_SHOT2, momentumCost: 0, hitlagBonus: 10, label: 'Tipper', tierColor: 0xff0044, instantFire: true },
  ],
});
